import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NO_RANK = "__none__";

export function ModAgents() {
  const { tr } = useLang();
  const { apiErr } = useAuth();
  const [users, setUsers] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", rank: NO_RANK });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/ranks").then((r) => setRanks(r.data));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      await api.post("/admin/users", {
        email: form.email.trim(),
        name: form.name.trim(),
        password: form.password,
        rank: form.rank === NO_RANK ? null : form.rank,
      });
      toast.success(tr.mCreated2);
      setForm({ email: "", name: "", password: "", rank: NO_RANK });
      load();
    } catch (err) {
      setError(apiErr(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const assignRank = async (id, rank) => {
    await api.patch(`/admin/users/${id}`, { rank: rank === NO_RANK ? "" : rank });
    toast.success(tr.mSaved);
    load();
  };

  const toggleActive = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { active: !u.active });
    load();
  };

  const del = async (id) => {
    await api.delete(`/admin/users/${id}`);
    toast.success(tr.mDelete);
    load();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* list */}
      <div className="overflow-hidden rounded-lg border border-zinc-800/70 bg-[#111113]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-left">
            <thead>
              <tr className="border-b border-zinc-800/70 bg-zinc-950/40 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-2.5 font-semibold">{tr.mAgentName}</th>
                <th className="px-4 py-2.5 font-semibold">{tr.mEmail}</th>
                <th className="px-4 py-2.5 font-semibold">{tr.mRole}</th>
                <th className="px-4 py-2.5 font-semibold">{tr.mAssignRank}</th>
                <th className="px-4 py-2.5 font-semibold"></th>
              </tr>
            </thead>
            <tbody data-testid="agents-body">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-zinc-800/50 text-[13px]">
                  <td className="px-4 py-2.5 text-zinc-200">{u.name}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded border px-1.5 py-0.5 text-[11px] ${u.role === "admin" ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-zinc-700 bg-zinc-800/50 text-zinc-300"}`}>
                      {u.role === "admin" ? tr.mAdmin : tr.mAgentRole}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {u.role === "admin" ? (
                      <span className="text-[12px] text-zinc-500">—</span>
                    ) : (
                      <Select value={u.rank || NO_RANK} onValueChange={(v) => assignRank(u.id, v)}>
                        <SelectTrigger data-testid={`rank-select-${u.id}`} className="h-8 w-40 border-zinc-800 bg-zinc-950 text-[12px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-800 bg-zinc-900">
                          <SelectItem value={NO_RANK} className="text-[12px] focus:bg-zinc-800">{tr.mNoRank}</SelectItem>
                          {ranks.map((r) => (
                            <SelectItem key={r.id} value={r.name} className="text-[12px] focus:bg-zinc-800">{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {u.role !== "admin" && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`} className={`rounded border px-2 py-1 text-[11px] ${u.active ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-500"}`}>
                          {u.active ? tr.mActive : tr.mDisable}
                        </button>
                        <button onClick={() => del(u.id)} data-testid={`delete-agent-${u.id}`} className="text-zinc-500 hover:text-red-400">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* create */}
      <form onSubmit={create} className="h-fit rounded-lg border border-zinc-800/70 bg-[#111113] p-5" data-testid="create-agent-form">
        <div className="mb-4 flex items-center gap-2 text-[13px] font-semibold text-zinc-100">
          <UserPlus size={16} className="text-blue-400" /> {tr.mCreateAgent}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mAgentName}</Label>
            <Input data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="border-zinc-800 bg-zinc-950 text-[13px]" />
          </div>
          <div>
            <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mAgentEmail}</Label>
            <Input data-testid="agent-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="border-zinc-800 bg-zinc-950 text-[13px]" />
          </div>
          <div>
            <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mAgentPassword}</Label>
            <Input data-testid="agent-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="border-zinc-800 bg-zinc-950 text-[13px]" />
          </div>
          <div>
            <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mAssignRank}</Label>
            <Select value={form.rank} onValueChange={(v) => setForm({ ...form, rank: v })}>
              <SelectTrigger data-testid="agent-rank" className="border-zinc-800 bg-zinc-950 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value={NO_RANK} className="text-[13px] focus:bg-zinc-800">{tr.mNoRank}</SelectItem>
                {ranks.map((r) => (
                  <SelectItem key={r.id} value={r.name} className="text-[13px] focus:bg-zinc-800">{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <div className="rounded-md border border-red-600/40 bg-red-600/10 px-3 py-2 text-[12px] text-red-300">{error}</div>}
          <Button data-testid="submit-agent" type="submit" disabled={creating} className="h-10 w-full bg-blue-600 text-[13px] text-white hover:bg-blue-700">
            {creating ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} {tr.mCreateAgent}
          </Button>
        </div>
      </form>
    </div>
  );
}
